import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Users, 
  MessageCircle, 
  Calendar, 
  User,
  BarChart3,
  Wrench,
  Shield,
  Building,
  Trophy,
  Bell,
  Search,
  Settings,
  Baby,
  ClipboardList
} from 'lucide-react';
import { useUserMenuData } from '@/components/UserMenu/useUserMenuData';

const AccountSpecificBottomNav = () => {
  const location = useLocation();
  const { role, isInternalUser, profileRoute } = useUserMenuData();
  
  const isActive = (path: string) => location.pathname === path;

  // Get navigation items based on user role
  const getNavItemsForRole = () => {
    const userRole = role?.toLowerCase() || 'player';

    switch (userRole) {
      case 'coach':
        return [
          { icon: Home, label: 'Home', path: '/dashboard' },
          { icon: ClipboardList, label: 'Forms', path: '/my-game-forms' },
          { icon: Calendar, label: 'Games', path: '/games/discover' },
          { icon: BarChart3, label: 'Analytics', path: '/coach-analytics' },
          { icon: Calendar, label: 'Manage', path: '/game-management' },
        ];

      case 'venue':
        return [
          { icon: Home, label: 'Home', path: '/dashboard' },
          { icon: ClipboardList, label: 'Forms', path: '/my-game-forms' },
          { icon: Calendar, label: 'Games', path: '/game-management' },
          { icon: BarChart3, label: 'Analytics', path: '/venue-analytics' },
          { icon: Building, label: 'Bookings', path: '/venue-bookings' },
        ];

      case 'admin':
        return [
          { icon: Home, label: 'Home', path: '/dashboard' },
          { icon: Users, label: 'Users', path: '/admin/users' },
          { icon: BarChart3, label: 'Analytics', path: '/admin/analytics' },
          { icon: Shield, label: 'Health', path: '/admin/system-health' },
          { icon: Settings, label: 'Settings', path: '/admin/platform-settings' },
        ];

      case 'parent':
        return [
          { icon: Home, label: 'Home', path: '/dashboard' },
          { icon: Baby, label: 'Children', path: '/child-management' },
          { icon: Shield, label: 'Safety', path: '/parental-settings' },
          { icon: User, label: 'Profile', path: profileRoute },
        ];

      case 'fan':
        return [
          { icon: Home, label: 'Home', path: '/dashboard' },
          { icon: Users, label: 'Teams', path: '/teams' },
          { icon: Calendar, label: 'Events', path: '/events' },
          { icon: Trophy, label: 'News', path: '/news' },
          { icon: MessageCircle, label: 'Social', path: '/community' },
        ];

      case 'creative':
        return [
          { icon: Home, label: 'Home', path: '/dashboard' },
          { icon: Users, label: 'Network', path: '/connections' },
          { icon: Trophy, label: 'Portfolio', path: '/portfolio' },
          { icon: MessageCircle, label: 'Lockeroom', path: '/chat' },
          { icon: User, label: 'Profile', path: profileRoute },
        ];

      case 'support':
        return [
          { icon: Home, label: 'Home', path: '/dashboard' },
          { icon: Calendar, label: 'Games', path: '/game-management' },
          { icon: BarChart3, label: 'Dashboard', path: '/scorekeeper/dashboard' },
          { icon: Bell, label: 'Apps', path: '/scorekeeper/applications' },
          { icon: User, label: 'Profile', path: profileRoute },
        ];

      case 'team-owner':
        return [
          { icon: Home, label: 'Home', path: '/dashboard' },
          { icon: Users, label: 'Teams', path: '/teams' },
          { icon: Calendar, label: 'Games', path: '/games/discover' },
          { icon: BarChart3, label: 'Analytics', path: '/team-analytics' },
          { icon: Settings, label: 'Manage', path: '/game-management' },
        ];

      default: // player and scorekeeper
        const baseItems = [
          { icon: Home, label: 'Home', path: '/dashboard' },
          { icon: ClipboardList, label: 'Forms', path: '/my-game-forms' },
          { icon: Users, label: 'Teams', path: '/teams' },
          { icon: Trophy, label: 'Stats', path: '/player-stats' },
          { icon: User, label: 'Profile', path: profileRoute },
        ];

        // Add scorekeeper-specific items - removed score approvals as it's not relevant for scorekeepers
        if (userRole === 'scorekeeper') {
          baseItems[2] = { icon: BarChart3, label: 'Dashboard', path: '/scorekeeper/dashboard' };
          baseItems[3] = { icon: Calendar, label: 'Apps', path: '/scorekeeper/applications' };
        }

        return baseItems;
    }
  };

  const navItems = getNavItemsForRole();

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-black border-t border-gray-800 px-2 py-2 z-50 md:hidden">
      <div className="flex justify-around items-center">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActiveItem = isActive(item.path);
          
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center py-1 px-1 min-w-0 flex-1 ${
                isActiveItem ? 'text-sports-green' : 'text-gray-400'
              }`}
            >
              <Icon className="h-5 w-5" />
              <span className="text-xs mt-1 truncate max-w-full">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default AccountSpecificBottomNav;